/**
 * ALGERIAN COMPLIANCE OBLIGATIONS DATABASE
 * 
 * Comprehensive database of tax and legal obligations for Algerian businesses
 * Based on 2024-2025 Finance Laws and DGI regulations
 * 
 * Sources:
 * - Finance Law 2024 (TAP abolished)
 * - Finance Law 2025 (new deadlines)
 * - CNAS regulations 2024-2025
 * - DGI (Direction Générale des Impôts) circulars
 * - CNRC requirements
 */

export interface ComplianceObligation {
  id: string;
  code: string; // G50, CA12, IFU, etc.
  
  // Names
  name_fr: string;
  name_ar: string;
  
  // Description
  description_fr: string;
  description_ar: string;
  
  // Frequency
  frequency: 'monthly' | 'quarterly' | 'annual' | 'biannual' | 'one_time';
  deadline_day: number; // Day of month (1-31)
  deadline_month?: number; // For annual (1-12)
  
  // Applicability Rules
  applies_to_all?: boolean;
  applies_to_legal_forms?: string[]; // ['SARL', 'EURL', 'SPA', 'EI', 'SNC', 'SCS']
  applies_to_tax_regimes?: string[]; // ['Réel', 'IFU', 'IRG']
  applies_to_activities?: string[]; // ['production', 'services', 'commerce', etc.]
  
  // Conditions
  min_employees?: number;
  min_revenue?: number; // in DZD
  max_revenue?: number; // for IFU threshold
  requires_export?: boolean;
  requires_import?: boolean;
  
  // Penalties
  has_penalty: boolean;
  penalty_description_fr?: string;
  penalty_description_ar?: string;
  penalty_amount?: number; // Base penalty in DZD
  late_payment_interest?: number; // Percentage per month
  
  // Authority
  authority: 'DGI' | 'CNAS' | 'CASNOS' | 'CNR' | 'CNRC' | 'MF';
  submission_method: 'online' | 'physical' | 'both';
  platform_url?: string;
  
  // Tags for filtering
  category: 'tax' | 'social' | 'legal' | 'accounting';
  tags: string[];
  
  // Status
  is_active: boolean;
  effective_from: string; // ISO date
  effective_until?: string; // ISO date
  
  // Additional info
  notes_fr?: string;
  notes_ar?: string;
}

/**
 * COMPREHENSIVE ALGERIAN COMPLIANCE OBLIGATIONS
 * Updated for 2024-2025
 */
export const ALGERIAN_COMPLIANCE_OBLIGATIONS: ComplianceObligation[] = [
  
  // ============================================
  // MONTHLY TAX OBLIGATIONS
  // ============================================
  
  {
    id: 'g50-monthly-salaries',
    code: 'G50',
    name_fr: 'Déclaration G50 (Série G) - Salaires',
    name_ar: 'التصريح G50 - الأجور',
    description_fr: 'Déclaration mensuelle des salaires avec retenues à la source (IRG/salaires). Obligatoire pour tous les employeurs dès le premier salarié.',
    description_ar: 'التصريح الشهري بالأجور مع الخصم من المنبع (IRG). إلزامي لجميع أصحاب العمل',
    frequency: 'monthly',
    deadline_day: 20,
    applies_to_all: false,
    min_employees: 1,
    has_penalty: true,
    penalty_description_fr: 'Pénalité de 25% + intérêts de retard 3% par mois',
    penalty_amount: 0, // Percentage-based
    late_payment_interest: 3,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.mfdgi.gov.dz',
    category: 'tax',
    tags: ['salaries', 'IRG', 'withholding', 'mandatory'],
    is_active: true,
    effective_from: '2024-01-01',
  },
  
  {
    id: 'ca12-vat-monthly',
    code: 'CA12',
    name_fr: 'Déclaration CA12 - TVA Mensuelle',
    name_ar: 'التصريح CA12 - الرسم على القيمة المضافة',
    description_fr: 'Déclaration mensuelle de la TVA collectée et déductible. Taux normal 19%, taux réduit 9%.',
    description_ar: 'التصريح الشهري بالرسم على القيمة المضافة. معدل عادي 19%، معدل مخفض 9%',
    frequency: 'monthly',
    deadline_day: 20,
    applies_to_all: true,
    applies_to_legal_forms: ['SARL', 'EURL', 'SPA', 'SNC', 'SCS', 'EI'],
    min_revenue: 30000000, // 30M DZD VAT registration threshold
    has_penalty: true,
    penalty_description_fr: 'Pénalité de 25% + intérêts de retard 3% par mois + amende 15,000 DZD',
    penalty_amount: 15000,
    late_payment_interest: 3,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.mfdgi.gov.dz',
    category: 'tax',
    tags: ['VAT', 'TVA', 'mandatory', 'monthly'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Seuil de 30M DZD pour l\'assujettissement obligatoire à la TVA',
  },
  
  {
    id: 'ifu-monthly',
    code: 'IFU',
    name_fr: 'IFU - Impôt Forfaitaire Unique',
    name_ar: 'الضريبة الجزافية الوحيدة',
    description_fr: 'Régime simplifié pour CA < 8M DZD. Taux: 5% (auto-entrepreneur: 0.5% depuis 2024)',
    description_ar: 'نظام مبسط للأعمال الصغيرة. CA < 8 مليون دج',
    frequency: 'monthly',
    deadline_day: 20,
    applies_to_legal_forms: ['EI'],
    applies_to_tax_regimes: ['IFU'],
    max_revenue: 8000000, // 8M DZD
    has_penalty: true,
    penalty_description_fr: 'Majoration de 25% + intérêts 3%/mois',
    late_payment_interest: 3,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.mfdgi.gov.dz',
    category: 'tax',
    tags: ['IFU', 'simplified', 'small-business'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Auto-entrepreneur: 0.5% (LF 2024). Professions libérales: 12%',
  },
  
  {
    id: 'cnas-monthly',
    code: 'CNAS-DAC',
    name_fr: 'CNAS - Déclaration d\'Assiette de Cotisation',
    name_ar: 'CNAS - تصريح وعاء الاشتراكات',
    description_fr: 'Cotisations sociales mensuelles: Employeur 26% + Employé 9%. Total 35% du salaire brut.',
    description_ar: 'الاشتراكات الاجتماعية الشهرية: صاحب العمل 26% + الموظف 9%',
    frequency: 'monthly',
    deadline_day: 20,
    applies_to_all: false,
    min_employees: 1,
    has_penalty: true,
    penalty_description_fr: 'Majoration de 10% + intérêts de retard',
    penalty_amount: 0,
    late_payment_interest: 2,
    authority: 'CNAS',
    submission_method: 'online',
    platform_url: 'https://teledeclaration.cnas.dz',
    category: 'social',
    tags: ['CNAS', 'social-security', 'mandatory', 'salaries'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Paiement avant le 20 du mois suivant. Employeur: 26%, Employé: 9%',
  },
  
  {
    id: 'etat-104-monthly',
    code: 'État 104',
    name_fr: 'État 104 - Retenues à la Source',
    name_ar: 'الحالة 104 - الخصم من المنبع',
    description_fr: 'Déclaration des retenues à la source sur honoraires et prestations de services.',
    description_ar: 'الإعلان عن الخصومات من المصدر على الأتعاب والخدمات',
    frequency: 'monthly',
    deadline_day: 20,
    applies_to_all: true,
    has_penalty: true,
    penalty_description_fr: 'Pénalité 25% des retenues non versées + intérêts 3%',
    late_payment_interest: 3,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.mfdgi.gov.dz',
    category: 'tax',
    tags: ['withholding', 'services', 'État 104'],
    is_active: true,
    effective_from: '2024-01-01',
  },
  
  // ============================================
  // QUARTERLY OBLIGATIONS
  // ============================================
  
  {
    id: 'ifu-quarterly-small',
    code: 'IFU-Q',
    name_fr: 'IFU Trimestriel (petites entreprises)',
    name_ar: 'IFU ربع سنوي',
    description_fr: 'Déclaration trimestrielle IFU pour entreprises < 10 employés',
    description_ar: 'التصريح الربع سنوي للشركات الصغيرة',
    frequency: 'quarterly',
    deadline_day: 20,
    applies_to_tax_regimes: ['IFU'],
    max_revenue: 8000000,
    has_penalty: true,
    penalty_description_fr: 'Majoration 25% + intérêts',
    late_payment_interest: 3,
    authority: 'DGI',
    submission_method: 'online',
    category: 'tax',
    tags: ['IFU', 'quarterly', 'small-business'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Optionnel pour < 10 employés. Trimestriel au lieu de mensuel.',
  },
  
  // ============================================
  // ANNUAL OBLIGATIONS
  // ============================================
  
  {
    id: 'bilan-annual',
    code: 'G4',
    name_fr: 'Bilan Comptable et Liasse Fiscale (G4)',
    name_ar: 'الميزانية المحاسبية والملف الضريبي',
    description_fr: 'Dépôt du bilan annuel, compte de résultat, et annexes. Formulaire G4 pour sociétés soumises à l\'IBS.',
    description_ar: 'إيداع الميزانية السنوية وحساب النتائج والملاحق',
    frequency: 'annual',
    deadline_day: 30,
    deadline_month: 4, // April 30 (extended to June 1 in 2024/2025)
    applies_to_all: true,
    applies_to_legal_forms: ['SARL', 'EURL', 'SPA', 'SNC', 'SCS'],
    has_penalty: true,
    penalty_description_fr: 'Amende 50,000 DZD à 2M DZD selon retard',
    penalty_amount: 50000,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.mfdgi.gov.dz',
    category: 'accounting',
    tags: ['bilan', 'annual', 'mandatory', 'accounting'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Deadline: 30 avril (souvent prolongé à fin mai/début juin). Formulaire G4 + annexes.',
  },
  
  {
    id: 'g1-personal-income',
    code: 'G1',
    name_fr: 'Déclaration G1 - IRG Annuelle',
    name_ar: 'التصريح G1 - الضريبة على الدخل الإجمالي',
    description_fr: 'Déclaration annuelle de l\'IRG pour les revenus personnels (BIC, BNC, fonciers, etc.)',
    description_ar: 'الإعلان السنوي عن ضريبة الدخل الإجمالي',
    frequency: 'annual',
    deadline_day: 30,
    deadline_month: 6, // Extended to June 30 in FL 2025
    applies_to_legal_forms: ['EI'],
    has_penalty: true,
    penalty_description_fr: 'Majoration 10% à 25% selon retard',
    late_payment_interest: 3,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.mfdgi.gov.dz',
    category: 'tax',
    tags: ['IRG', 'personal-income', 'annual'],
    is_active: true,
    effective_from: '2025-01-01',
    notes_fr: 'Deadline prolongé à 30 juin par LF 2025 (au lieu du 30 avril)',
  },
  
  {
    id: 'g29-annual-salaries',
    code: 'G29',
    name_fr: 'Déclaration G29 - DAS Annuelle',
    name_ar: 'التصريح G29 - التصريح السنوي بالأجور',
    description_fr: 'Déclaration Annuelle des Salaires (DAS). Récapitulatif annuel des salaires versés et IRG retenu.',
    description_ar: 'الإعلان السنوي للرواتب - ملخص الأجور المدفوعة',
    frequency: 'annual',
    deadline_day: 31,
    deadline_month: 1, // January 31
    applies_to_all: false,
    min_employees: 1,
    has_penalty: true,
    penalty_description_fr: 'Amende administrative + sanctions CNAS',
    penalty_amount: 15000,
    authority: 'DGI',
    submission_method: 'online',
    category: 'tax',
    tags: ['salaries', 'annual', 'DAS', 'IRG'],
    is_active: true,
    effective_from: '2024-01-01',
  },
  
  {
    id: 'cnas-das-annual',
    code: 'CNAS-DAS',
    name_fr: 'CNAS - DAS (Déclaration Annuelle des Salaires)',
    name_ar: 'CNAS - التصريح السنوي بالأجور',
    description_fr: 'Déclaration annuelle obligatoire des salaires auprès de la CNAS. Deadline: 31 janvier.',
    description_ar: 'الإعلان السنوي الإلزامي عن الرواتب لدى CNAS. الموعد النهائي: 31 يناير',
    frequency: 'annual',
    deadline_day: 31,
    deadline_month: 1,
    applies_to_all: false,
    min_employees: 1,
    has_penalty: true,
    penalty_description_fr: 'Fermeture automatique du compte employeur en cas de double déclaration',
    authority: 'CNAS',
    submission_method: 'online',
    platform_url: 'https://teledeclaration.cnas.dz',
    category: 'social',
    tags: ['CNAS', 'DAS', 'annual', 'salaries'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'À faire entre le 1er et 31 janvier de chaque année via le portail CNAS',
  },
  
  {
    id: 'transfer-pricing-doc',
    code: 'TP-DOC',
    name_fr: 'Documentation Prix de Transfert',
    name_ar: 'وثائق تسعير التحويل',
    description_fr: 'Documentation obligatoire des prix de transfert pour transactions avec entités liées.',
    description_ar: 'وثائق إلزامية لتسعير التحويل للمعاملات مع الكيانات ذات الصلة',
    frequency: 'annual',
    deadline_day: 31,
    deadline_month: 5, // Extended to May 31 / June 1-2
    applies_to_legal_forms: ['SARL', 'EURL', 'SPA'],
    min_revenue: 150000000, // 150M DZD threshold
    has_penalty: true,
    penalty_description_fr: 'Amende de 15M DZD (montant fortement augmenté en 2023)',
    penalty_amount: 15000000,
    authority: 'DGI',
    submission_method: 'online',
    platform_url: 'https://www.jibayatic.dz',
    category: 'tax',
    tags: ['transfer-pricing', 'multinationals', 'related-parties'],
    is_active: true,
    effective_from: '2023-01-01',
    notes_fr: 'Soumission via plateforme Jibayatic. Amende augmentée à 15M DZD en 2023.',
  },
  
  {
    id: 'ag-annual-meeting',
    code: 'AG',
    name_fr: 'Assemblée Générale Annuelle',
    name_ar: 'الجمعية العامة السنوية',
    description_fr: 'Assemblée générale annuelle des associés. Approbation des comptes et décisions importantes.',
    description_ar: 'الجمعية العامة السنوية للشركاء. الموافقة على الحسابات والقرارات الهامة',
    frequency: 'annual',
    deadline_day: 30,
    deadline_month: 6, // Within 6 months of fiscal year end
    applies_to_legal_forms: ['SARL', 'EURL', 'SPA', 'SCS'],
    has_penalty: false,
    authority: 'CNRC',
    submission_method: 'physical',
    category: 'legal',
    tags: ['AG', 'shareholders', 'governance'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Dans les 6 mois suivant la clôture de l\'exercice. PV à déposer au CNRC.',
  },
  
  // ============================================
  // ONE-TIME / REGISTRATION OBLIGATIONS
  // ============================================
  
  {
    id: 'cnrc-registration',
    code: 'CNRC-REG',
    name_fr: 'Inscription au Registre de Commerce (CNRC)',
    name_ar: 'التسجيل في السجل التجاري',
    description_fr: 'Inscription obligatoire au Centre National du Registre de Commerce. Obtention du RC.',
    description_ar: 'التسجيل الإلزامي في المركز الوطني للسجل التجاري',
    frequency: 'one_time',
    deadline_day: 15, // Within 15 days of creation
    applies_to_all: true,
    has_penalty: true,
    penalty_description_fr: 'Amende 5,000 à 5M DZD selon la loi 04-08',
    penalty_amount: 5000,
    authority: 'CNRC',
    submission_method: 'online',
    platform_url: 'https://sidjilcom.cnrc.dz',
    category: 'legal',
    tags: ['registration', 'RC', 'one-time', 'mandatory'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Obligatoire dans les 15 jours suivant la création de l\'entreprise',
  },
  
  {
    id: 'dgi-registration',
    code: 'NIF',
    name_fr: 'Obtention du NIF (DGI)',
    name_ar: 'الحصول على رقم التعريف الجبائي',
    description_fr: 'Inscription auprès de la Direction Générale des Impôts. Obtention du NIF (15 chiffres).',
    description_ar: 'التسجيل لدى المديرية العامة للضرائب. الحصول على NIF',
    frequency: 'one_time',
    deadline_day: 30,
    applies_to_all: true,
    has_penalty: true,
    penalty_description_fr: 'Impossibilité d\'exercer légalement l\'activité',
    authority: 'DGI',
    submission_method: 'both',
    category: 'legal',
    tags: ['registration', 'NIF', 'tax-id', 'one-time'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Le NIF est requis pour toutes les démarches fiscales',
  },
  
  {
    id: 'cnas-employer-registration',
    code: 'CNAS-REG',
    name_fr: 'Affiliation Employeur CNAS',
    name_ar: 'تسجيل صاحب العمل في CNAS',
    description_fr: 'Affiliation de l\'employeur auprès de la CNAS. Obligatoire dès l\'embauche du premier salarié.',
    description_ar: 'تسجيل صاحب العمل لدى CNAS. إلزامي عند توظيف أول موظف',
    frequency: 'one_time',
    deadline_day: 10, // Within 10 days of first hire
    applies_to_all: false,
    min_employees: 1,
    has_penalty: true,
    penalty_description_fr: 'Pénalités + impossibilité de déclarer les salariés',
    authority: 'CNAS',
    submission_method: 'online',
    platform_url: 'https://teledeclaration.cnas.dz',
    category: 'social',
    tags: ['CNAS', 'registration', 'employer', 'one-time'],
    is_active: true,
    effective_from: '2024-01-01',
    notes_fr: 'Affiliation dans les 10 jours suivant l\'embauche du 1er salarié',
  },
  
  {
    id: 'employee-cnas-registration',
    code: 'CNAS-EMP',
    name_fr: 'Déclaration d\'embauche (CNAS)',
    name_ar: 'إعلان التوظيف (CNAS)',
    description_fr: 'Déclaration de chaque nouvel employé auprès de la CNAS dans les 10 jours de l\'embauche.',
    description_ar: 'الإعلان عن كل موظف جديد لدى CNAS خلال 10 أيام',
    frequency: 'one_time',
    deadline_day: 10,
    applies_to_all: false,
    min_employees: 1,
    has_penalty: true,
    penalty_description_fr: 'Amende administrative + cotisations arriérées',
    authority: 'CNAS',
    submission_method: 'online',
    platform_url: 'https://teledeclaration.cnas.dz',
    category: 'social',
    tags: ['CNAS', 'hiring', 'employees'],
    is_active: true,
    effective_from: '2024-01-01',
  },
  
  // ============================================
  // ABOLISHED / REMOVED (kept for historical reference)
  // ============================================
  
  {
    id: 'tap-abolished',
    code: 'TAP',
    name_fr: 'TAP - Taxe sur l\'Activité Professionnelle (SUPPRIMÉE)',
    name_ar: 'TAP - الضريبة على النشاط المهني (ملغاة)',
    description_fr: 'Taxe de 1,5% sur le CA. SUPPRIMÉE par la Loi de Finances 2024.',
    description_ar: 'ضريبة 1.5% على رقم الأعمال. ملغاة بموجب قانون المالية 2024',
    frequency: 'monthly',
    deadline_day: 20,
    applies_to_all: true,
    has_penalty: false,
    authority: 'DGI',
    submission_method: 'online',
    category: 'tax',
    tags: ['TAP', 'abolished', 'historical'],
    is_active: false,
    effective_from: '2000-01-01',
    effective_until: '2023-12-31',
    notes_fr: 'ABOLIE par la Loi de Finances 2024. Ne plus déclarer depuis le 1er janvier 2024.',
  },
];

/**
 * Get all active obligations
 */
export function getActiveObligations(): ComplianceObligation[] {
  return ALGERIAN_COMPLIANCE_OBLIGATIONS.filter(o => o.is_active);
}

/**
 * Get obligations by frequency
 */
export function getObligationsByFrequency(frequency: string): ComplianceObligation[] {
  return getActiveObligations().filter(o => o.frequency === frequency);
}

/**
 * Get obligations by authority
 */
export function getObligationsByAuthority(authority: string): ComplianceObligation[] {
  return getActiveObligations().filter(o => o.authority === authority);
}

/**
 * Get obligations by category
 */
export function getObligationsByCategory(category: string): ComplianceObligation[] {
  return getActiveObligations().filter(o => o.category === category);
}
